import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, Home } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | pending | error
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await api.get(`/payments/checkout/status/${sessionId}`);
        setData(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= 6) {
          setStatus("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 6) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="payment-success-page"
      className="min-h-screen flex items-center justify-center bg-[#F4F1EA] p-6"
    >
      <div className="max-w-lg w-full bg-white border-2 border-[#0B1810] shadow-[10px_10px_0_#FF5A00] p-8 md:p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-[#FF5A00] animate-spin" size={56} />
            <h1 className="font-heading text-4xl md:text-5xl text-[#0B1810] mt-6">
              Confirming Payment
            </h1>
            <p className="mt-3 text-[#4A4D4A] font-body">
              Hold tight while we verify with Stripe...
            </p>
          </>
        )}

        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[#FF5A00]" size={64} />
            <h1 className="font-heading text-4xl md:text-5xl text-[#0B1810] mt-6">
              Payment Successful
            </h1>
            <p className="mt-3 text-[#4A4D4A] font-body">
              Thank you! A receipt has been emailed to you.
            </p>
            {data && (
              <div className="mt-6 border-t border-[#D1CFC7] pt-6 text-left space-y-2">
                <div className="flex justify-between">
                  <span className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                    Amount
                  </span>
                  <span className="font-heading text-xl text-[#0B1810]">
                    ${data.amount.toFixed(2)} {data.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                    Status
                  </span>
                  <span className="font-heading text-xl text-[#FF5A00] uppercase">
                    Paid
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {status === "pending" && (
          <>
            <Loader2 className="mx-auto text-[#FF5A00]" size={56} />
            <h1 className="font-heading text-4xl md:text-5xl text-[#0B1810] mt-6">
              Still Processing
            </h1>
            <p className="mt-3 text-[#4A4D4A] font-body">
              Your payment is taking a moment. Check your email — we'll send a
              receipt as soon as it confirms.
            </p>
          </>
        )}

        {status === "expired" && (
          <>
            <AlertCircle className="mx-auto text-[#0B1810]" size={56} />
            <h1 className="font-heading text-4xl md:text-5xl text-[#0B1810] mt-6">
              Session Expired
            </h1>
            <p className="mt-3 text-[#4A4D4A] font-body">
              The checkout session expired. Please try again.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle className="mx-auto text-[#0B1810]" size={56} />
            <h1 className="font-heading text-4xl md:text-5xl text-[#0B1810] mt-6">
              Something Went Wrong
            </h1>
            <p className="mt-3 text-[#4A4D4A] font-body">
              We couldn't confirm the payment. If you were charged, call us at
              (321) 682-6085.
            </p>
          </>
        )}

        <Link
          data-testid="back-home-button"
          to="/"
          className="btn-primary mt-8 justify-center"
        >
          <Home size={18} /> Back to Home
        </Link>
      </div>
    </div>
  );
}
